import { TSmartAccountClient } from '@/types';
import { pimlicoClient, DEFAULT_TIMEOUT, getNonce } from '../account-utils';
import {
  CONTRACT_ABI_MAP,
  ContractMethod,
  ContractType,
  PortalContractEvent,
  publicClient,
} from '../contract-utils';
import { AgentInstance } from './smart-account-agent';
import { encodeFunctionData, Hex, parseEventLogs } from 'viem';

import * as ucans from '@ucans/ucans';
import { CheckAndTransferOwnershipSteps } from '@/components/identity-setup-modal/types';

const executeLegacyClientOperations = async (
  legacySmartAccountClient: TSmartAccountClient,
  portalAddress: Hex
) => {
  const callData = [];

  const isCollaborator = await publicClient.readContract({
    address: portalAddress,
    abi: CONTRACT_ABI_MAP[ContractType.Portal],
    functionName: 'isCollaborator',
    args: [AgentInstance.getAgentAddress()],
  });

  // Register new owner as collaborator call data
  if (!isCollaborator)
    callData.push({
      data: encodeFunctionData({
        abi: CONTRACT_ABI_MAP[ContractType.Portal],
        functionName: ContractMethod.AddCollaborator,
        args: [AgentInstance.getAgentAddress()],
      }),
      to: portalAddress,
      value: BigInt(0),
    });

  const pendingOwner = await publicClient.readContract({
    address: portalAddress,
    abi: CONTRACT_ABI_MAP[ContractType.Portal],
    functionName: 'pendingOwner',
  });

  // Transfer ownership call data if not owner
  if (pendingOwner !== AgentInstance.getAgentAddress()) {
    callData.push({
      data: encodeFunctionData({
        abi: CONTRACT_ABI_MAP[ContractType.Portal],
        functionName: ContractMethod.TransferOwnership,
        args: [AgentInstance.getAgentAddress()],
      }),
      to: portalAddress,
      value: BigInt(0),
    });
  }

  // Return all legacy operations are already done
  if (callData.length === 0) return;

  const encodedCallData =
    await legacySmartAccountClient.account.encodeCalls(callData);

  const txHash = await legacySmartAccountClient.sendUserOperation({
    callData: encodedCallData,
    callGasLimit: BigInt(5000000),
    nonce: getNonce(),
  });

  const receipt = await pimlicoClient.waitForUserOperationReceipt({
    hash: txHash,
    timeout: 1000000,
  });

  if (!receipt.success) throw new Error('Failed to perform legacy operations');

  const parsedLogs = parseEventLogs({
    abi: CONTRACT_ABI_MAP[ContractType.Portal],
    eventName: [
      PortalContractEvent.AddedCollaborator,
      PortalContractEvent.OwnershipTransferStarted,
    ],
    logs: receipt.logs,
  });

  if (parsedLogs.length < 2)
    throw new Error('Failed to perform legacy operations');
};

// TODO: remove logs

const executeNewClientOperations = async (
  portalAddress: Hex,
  editSecret: string,
  viewSecret: string
) => {
  const userOpRequests = [];
  const [viewDid, editDid] = (await publicClient.readContract({
    address: portalAddress,
    abi: CONTRACT_ABI_MAP[ContractType.Portal],
    functionName: 'collaboratorKeys',
    args: [AgentInstance.getAgentAddress()],
  })) as [string, string];

  const currentViewDid = ucans.EdKeypair.fromSecretKey(viewSecret).did();
  const currentEditDid = ucans.EdKeypair.fromSecretKey(editSecret).did();

  if (viewDid !== currentViewDid || editDid !== currentEditDid) {
    userOpRequests.push({
      contractAddress: portalAddress,
      data: encodeFunctionData({
        abi: CONTRACT_ABI_MAP[ContractType.Portal],
        functionName: ContractMethod.RegisterCollaboratorKeys,
        args: [currentViewDid, currentEditDid],
      }),
    });
  }

  userOpRequests.push({
    contractAddress: portalAddress,
    data: encodeFunctionData({
      abi: CONTRACT_ABI_MAP[ContractType.Portal],
      functionName: ContractMethod.AcceptOwnership,
    }),
  });

  const receipt = await AgentInstance.executeUserOperationRequest(
    userOpRequests,
    DEFAULT_TIMEOUT
  );

  if (!receipt.success)
    throw new Error('Failed to execute new client operations');

  const parsedLogs = parseEventLogs({
    abi: CONTRACT_ABI_MAP[ContractType.Portal],
    eventName: [
      PortalContractEvent.RegisteredCollaboratorKeys,
      PortalContractEvent.OwnershipTransferred,
    ],
    logs: receipt.logs,
  });

  if (parsedLogs.length < 2)
    throw new Error('Failed to execute new client operations');
};

export const checkAndTransferOwnership = async (
  legacySmartAccountClient: TSmartAccountClient,
  portalAddress: Hex,
  editSecret: string,
  viewSecret: string,
  onStepComplete: (step: CheckAndTransferOwnershipSteps) => void
) => {
  const portalOwner = await publicClient.readContract({
    address: portalAddress,
    abi: CONTRACT_ABI_MAP[ContractType.Portal],
    functionName: 'owner',
  });

  if (portalOwner === AgentInstance.getAgentAddress()) return;

  await executeLegacyClientOperations(legacySmartAccountClient, portalAddress);

  onStepComplete(CheckAndTransferOwnershipSteps.ACCEPT_OWNERSHIP);

  await executeNewClientOperations(portalAddress, editSecret, viewSecret);
};
